import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { useDateRangeFilter } from '../hooks/useDateRangeFilter';
import { useTableHeight } from '../../../hooks/tables/useTableHeight';
import moment from 'moment';
import {
  Box,
  CircularProgress,
  Grid,
  Paper,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  getWeeklyPayrollReport,
  getWeeklyPayrollReportCsvData,
  setWeeklyPayrollCsvData,
} from '../../../store/ReportsDetailsSlice/actions';
import {
  selectWeeklyPayrollReportData,
  selectWeeklyPayrollCsvReportData,
} from '../../../store/ReportsDetailsSlice/selectors';
import useColumns from './hooks/useColumns';
import useRows from './hooks/useRows';
import useColumnGroupingModel from './hooks/useColumnGroupingModel';
import { saveCsvToFile } from '../../../utils/saveCsvToFile';
import { ExportCsvButton } from '../../../components/Buttons/ExportCsvButton';
import ReportsHeader from '../../../components/Reports/ReportsHeader';

const WeeklyPayrollReport = () => {
  const dispatch = useDispatch();

  const { containerRef, height } = useTableHeight();

  const { weeklyPayrollReportData, loadingWeeklyPayrollReport } = useSelector(
    selectWeeklyPayrollReportData
  );

  const { weeklyPayrollCsvReport, isLoadingWeeklyPayrollCsvData } = useSelector(
    selectWeeklyPayrollCsvReportData
  );

  const columns = useColumns();
  const rows = useRows(weeklyPayrollReportData?.data);
  const columnGroupingModel = useColumnGroupingModel(weeklyPayrollReportData);

  const { fromDate, toDate, render: renderDateFilter } = useDateRangeFilter();

  useEffect(() => {
    if (fromDate && toDate) {
      const start_dt = moment(fromDate).format('YYYY-MM-DD') + ' 00:00:00';
      const end_dt = moment(toDate).format('YYYY-MM-DD') + ' 23:59:59';

      const params = {
        start_dt,
        end_dt,
      };

      dispatch(getWeeklyPayrollReport(params));
    }
  }, [dispatch, fromDate, toDate]);

  const handleDownloadReport = () => {
    if (fromDate && toDate) {
      const start_dt = moment(fromDate).format('YYYY-MM-DD') + ' 00:00:00';
      const end_dt = moment(toDate).format('YYYY-MM-DD') + ' 23:59:59';

      const params = {
        start_dt,
        end_dt,
        csv: 1,
      };

      dispatch(getWeeklyPayrollReportCsvData(params));
    }
  };

  useEffect(() => {
    if (weeklyPayrollCsvReport?.data) {
      saveCsvToFile(weeklyPayrollCsvReport.data, weeklyPayrollCsvReport.filename);
      dispatch(setWeeklyPayrollCsvData({ data: null, filename: '' }))
    }
  }, [weeklyPayrollCsvReport, dispatch]);

  return (
    <Paper className="p-4" sx={{ marginTop: '1rem' }}>
      <ReportsHeader title="Complete Weekly Payroll Report (North Smithfield)" />

      <Grid container spacing={2}>
        {renderDateFilter}
      </Grid>

      <Box sx={{ marginTop: '1rem' }}>
        <ExportCsvButton
          onClick={handleDownloadReport}
          disabled={isLoadingWeeklyPayrollCsvData}
          text={'Export CSV'}
        />
      </Box>

      {loadingWeeklyPayrollReport ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '200px',
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <Box
          ref={containerRef}
          sx={{ height, marginTop: '1rem', width: '100%' }}
        >
          {!!rows.length ? (
            <DataGrid
              rows={rows}
              columns={columns}
              experimentalFeatures={{ columnGrouping: true }}
              columnGroupingModel={columnGroupingModel}
              disableRowSelectionOnClick
              disableColumnMenu={true}
              hideFooter={true}
              density="compact"
              rowHeight={32}
              sx={{
                '& .MuiDataGrid-columnHeaderTitle': {
                  fontWeight: 'bold',
                  textAlign: 'start',
                  justifyContent: 'flex-start',
                  width: '100%',
                },
                '& .MuiDataGrid-cell:focus': {
                  outline: 'none',
                },
                '& .MuiDataGrid-cell:focus-within': {
                  outline: 'none',
                },
                '& .MuiDataGrid-cell': {
                  borderRight: '1px solid rgba(224, 224, 224, 1)',
                },
                '& .MuiDataGrid-columnHeader': {
                  borderRight: '1px solid rgba(224, 224, 224, 1)',
                  display: 'flex',
                  justifyContent: 'flex-start',
                },
                '& .MuiDataGrid-cell:last-child': {
                  borderRight: 'none',
                },
                '& .MuiDataGrid-columnHeader:last-child': {
                  borderRight: 'none',
                },
              }}
            />
          ) : (
            <Typography variant="body1" align="center">
              No data available for the selected date range.
            </Typography>
          )}
        </Box>
      )}
    </Paper>
  );
};

export default WeeklyPayrollReport;
